import {defineSlotRecipe} from '@chakra-ui/react3'

/**
 * Slot recipe for the animated hamburger ⇄ X glyph (STOK-17031 Step 11).
 *
 * The glyph is FIVE `<line>`s, mounted in both resting states: three horizontal
 * bars (`barTop`/`barMiddle`/`barBottom`) matching the design system's `menu`
 * glyph, plus the two diagonal strokes (`diagDown`/`diagUp`) of the `cancel` (X)
 * glyph. Their geometry is inlined (in `hamburger-icon.geometry.js`) from
 * `app/leap/assets/svg/menu.svg` and `close.svg`; `hamburger-icon.test.jsx`
 * asserts it stays in sync with those source files.
 *
 * Every line stays mounted: a CSS transition only runs from an already-painted
 * value, so lines mounted at press time can settle with no transition
 * (STOK-17627).
 *
 * ## Choreography (two reversible phases, from the STOK-17031 motion clip)
 *
 * Phase A — the three bars converge onto the centre line (top/bottom translate
 * to the middle) so they read as a single horizontal stroke. Phase B — that
 * line fades as the two diagonals bloom out of the centre: each rotates up from
 * horizontal WHILE growing from a point to full length (a "scissors" opening),
 * landing exactly on the X. Closing replays the exact reverse: the X retracts
 * to the centre, the single line reappears, then it splits back into three bars.
 *
 * This is driven entirely by staged CSS `transition`s — NOT `@keyframes`. Bars
 * transition `translate`/`opacity`; diagonals transition `rotate`/`scale`/
 * `opacity`. Per-property `transition-delay`s split the motion into the two
 * phases, and because the delay pattern is inverted between the open and closed
 * states, the reverse plays the phases back in the opposite order for free.
 * Transitions (unlike state-keyed keyframes) never replay on initial mount, so
 * both directions are symmetric without a mount-replay workaround.
 *
 * The diagonals carry their FINAL X coordinates as geometry; at rest they are
 * hidden by rotating them flat (`diagDown` −45°, `diagUp` +45°) and scaling to
 * the centre (`scale: 0`). `vectorEffect="non-scaling-stroke"` (set in the JSX)
 * keeps the stroke a constant round-capped width while `scale` grows the line,
 * so the bloom never distorts the stroke. `transformBox: view-box` +
 * `transformOrigin: center` pin every transform to the viewBox centre (12,12) —
 * the middle bar's line, and the X's crossing point.
 *
 * DESIGN-ISSUE(STOK-17031): the per-phase duration and the 50/50 split are
 * implemented to match the motion clip rather than a tokenised motion spec, so
 * the timing stays provisional. Easing reuses the header's existing motion
 * language (`cubic-bezier(0.5, 0, 0, 1)`, already used in `header/index.recipe.js` /
 * `flyout/index.recipe.js`).
 *
 * `_motionReduce` (`@media (prefers-reduced-motion: reduce)`) collapses the
 * transition to near-instant so the glyph still reaches the correct end state.
 */

// One choreography phase, in ms. Kept as a raw literal (mirrors the
// `durations.moderate` token, 200ms) because the per-property `transition-delay`s
// below MUST equal the duration for the two phases to abut seamlessly — a token
// lookup on the comma-separated delay lists would not resolve, and duration and
// delay must never drift apart.
const PHASE_MS = 200
const PHASE = `${PHASE_MS}ms`

const EASE = 'cubic-bezier(0.5, 0, 0, 1)'
// Travel from a rest bar (menu.svg y = 5.8 / 18.2) to the centre line (y = 12).
const CONVERGE = '6.2px'

// repeat inside every `_open` block: `_motionReduce` is a bare media query and
// adds no specificity, so an `_open` delay outranks it
const INSTANT = {
    transitionDuration: '0.01ms',
    transitionDelay: '0ms'
}

const barBase = {
    stroke: 'icons.primary',
    strokeWidth: '1.6',
    strokeLinecap: 'round',
    transformBox: 'view-box',
    transformOrigin: 'center',
    translate: '0 0',
    opacity: '1',
    transitionProperty: 'translate, opacity',
    transitionDuration: PHASE,
    transitionTimingFunction: EASE,
    // closed: spread back out (translate) in phase B, reappear (opacity) in
    // phase A. Property order is `translate, opacity`.
    transitionDelay: `${PHASE}, 0ms`,
    _motionReduce: INSTANT
}
// open: converge (translate) in phase A, fade out (opacity) in phase B.
const barOpenDelay = `0ms, ${PHASE}`

const diagBase = {
    stroke: 'icons.primary',
    strokeWidth: '1.35',
    strokeLinecap: 'round',
    transformBox: 'view-box',
    transformOrigin: 'center',
    // hidden at rest: collapsed to the centre point (the per-slot `rotate` below
    // lays it flat first so it unfolds from horizontal on the way in).
    scale: '0',
    opacity: '0',
    transitionProperty: 'rotate, scale, opacity',
    transitionDuration: PHASE,
    transitionTimingFunction: EASE,
    // closed: retract (rotate/scale/fade) in phase A. Order `rotate, scale, opacity`.
    transitionDelay: '0ms, 0ms, 0ms',
    _open: {
        rotate: '0deg',
        scale: '1',
        opacity: '1',
        // open: bloom in phase B, after the bars have converged.
        transitionDelay: `${PHASE}, ${PHASE}, ${PHASE}`,
        _motionReduce: INSTANT
    },
    _motionReduce: INSTANT
}

export const hamburgerIconRecipe = defineSlotRecipe({
    slots: ['root', 'barTop', 'barMiddle', 'barBottom', 'diagDown', 'diagUp'],
    base: {
        root: {
            display: 'block',
            flexShrink: '0',
            // match the token every `<line>` strokes with, so inherited
            // `currentColor` agrees
            color: 'icons.primary',
            // Fallback size; when nested in `HeaderIconButton` the button's
            // `_icon` selector re-sizes this <svg> to the 20px icon box.
            width: '5',
            height: '5'
        },
        barTop: {
            ...barBase,
            _open: {
                translate: `0 ${CONVERGE}`,
                opacity: '0',
                transitionDelay: barOpenDelay,
                _motionReduce: INSTANT
            }
        },
        barMiddle: {
            ...barBase,
            _open: {opacity: '0', transitionDelay: barOpenDelay, _motionReduce: INSTANT}
        },
        barBottom: {
            ...barBase,
            _open: {
                translate: `0 -${CONVERGE}`,
                opacity: '0',
                transitionDelay: barOpenDelay,
                _motionReduce: INSTANT
            }
        },
        // Rest rotation lays each diagonal flat so it unfolds from the centre
        // line into the X; `_open` (in `diagBase`) returns it to its authored ±45°.
        diagDown: {...diagBase, rotate: '-45deg'},
        diagUp: {...diagBase, rotate: '45deg'}
    }
})
